import React, { useState, useMemo } from "react";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import ChatWithUs from "../../components/ChatWithUs/ChatWithUs";
import { useInquiry } from "../../context/InquiryContext";
import "./InquiryPage.css";
import { generateInquiryPdf } from "../../utils/pdfGenerator";
import ProgressModal from "../../components/Loading/ProgressModal";

export default function InquiryPage() {
  const { items, updateInquiry, removeInquiry, clearInquiries, userProfile, saveUserProfile, validateUserProfile } = useInquiry();
  const [editingId, setEditingId] = useState(null);
  const [drafts, setDrafts] = useState({}); // per-item draft edits
  const [profileOpen, setProfileOpen] = useState(false); // toggles profile edit panel
  const [profileDraft, setProfileDraft] = useState(() => userProfile || {
    fullName: "",
    email: "",
    phone: "",
    companyName: "",
    businessType: "Importer",
    countries: [],
    taxId: "",
    incoterm: "FOB",
    currency: "USD",
    preferredPort: "",
    expectedQty: "",
    remarks: "",
    consent: true,
  });
  const [profileErrors, setProfileErrors] = useState({});

  // progress modal state
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfPercent, setPdfPercent] = useState(0);
  const [pdfPageIndex, setPdfPageIndex] = useState(0);
  const [pdfTotalPages, setPdfTotalPages] = useState(0);
  // Removed the unused `pdfAbortRequested` variable — keep the setter only to allow toggling if needed
  const [, setPdfAbortRequested] = useState(false);

  // Ensure profileDraft updates when userProfile changes externally
  React.useEffect(() => {
    setProfileDraft(userProfile || {
      fullName: "",
      email: "",
      phone: "",
      companyName: "",
      businessType: "Importer",
      countries: [],
      taxId: "",
      incoterm: "FOB",
      currency: "USD",
      preferredPort: "",
      expectedQty: "",
      remarks: "",
      consent: true,
    });
  }, [userProfile]);

  const startEdit = (item) => {
    setEditingId(item.id);
    setDrafts((d) => ({
      ...d,
      [item.id]: {
        additionalText: item.additionalText ?? item.requirements ?? "",
        selectedTags: Array.isArray(item.selectedTags) ? [...item.selectedTags] : [],
      },
    }));
  };

  const toggleDraftTag = (itemId, tag) => {
    setDrafts((d) => {
      const prev = d[itemId] || { selectedTags: [] };
      const curTags = Array.isArray(prev.selectedTags) ? [...prev.selectedTags] : [];
      if (curTags.includes(tag)) {
        return { ...d, [itemId]: { ...prev, selectedTags: curTags.filter((t) => t !== tag) } };
      }
      return { ...d, [itemId]: { ...prev, selectedTags: [...curTags, tag] } };
    });
  };

  const saveEdit = (id) => {
    const patch = drafts[id] || {};
    const updated = {
      additionalText: patch.additionalText,
      selectedTags: Array.isArray(patch.selectedTags) ? patch.selectedTags : [],
    };
    updateInquiry(id, updated);
    setEditingId(null);
  };

  // Validate profileDraft
  const validateProfileDraft = () => {
    const { ok, errors } = validateUserProfile(profileDraft);
    setProfileErrors(errors);
    return ok;
  };

  const saveProfile = () => {
    if (!validateProfileDraft()) {
      return;
    }
    saveUserProfile(profileDraft);
    setProfileOpen(false);
  };

  // Whether Generate PDF should be enabled
  const isProfileValid = useMemo(() => {
    const { ok } = validateUserProfile(userProfile || profileDraft);
    return ok;
  }, [userProfile, profileDraft, validateUserProfile]);

  const handleGeneratePDF = async () => {
    const profileToUse = userProfile || profileDraft;
    const { ok, errors } = validateUserProfile(profileToUse);
    if (!ok) {
      setProfileErrors(errors);
      setProfileOpen(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (!items || items.length === 0) {
      alert("No inquiry items available to generate PDF.");
      return;
    }

    setPdfOpen(true);
    setPdfPercent(0);
    setPdfPageIndex(0);
    setPdfTotalPages(0);
    setPdfAbortRequested(false);

    try {
      await generateInquiryPdf({
        profile: profileToUse,
        items,
        onProgress: (percent, pageIndex, total) => {
          setPdfPercent(percent);
          setPdfPageIndex(pageIndex);
          setPdfTotalPages(total);
        },
      });
    } catch (err) {
      console.error("PDF generation error:", err);
      alert("Failed to generate PDF. See console for details.");
    } finally {
      setPdfOpen(false);
    }
  };

  return (
    <>
      <Navbar />
      <main className="inquiry-page-container">
        <div className="inquiry-hero">
          <h1 className="inquiry-title">Your Inquiry List</h1>
          <p className="inquiry-sub">
            Review items you added — edit details or selected tags before generating the final PDF.
          </p>
        </div>

        {/* Profile summary + edit */}
        <section className="profile-card">
          <div className="profile-summary">
            <div>
              <strong>{userProfile?.fullName || profileDraft.fullName || "No name provided"}</strong>
              <div className="profile-sub">{userProfile?.companyName || profileDraft.companyName || ""}</div>
              <div className="profile-sub small">
                {userProfile?.email || profileDraft.email || "No email"} • {userProfile?.phone || profileDraft.phone || "No phone"}
              </div>
            </div>

            <div className="profile-actions">
              <button className="btn-outline" onClick={() => { setProfileOpen((s) => !s); }}>
                {profileOpen ? "Close" : (userProfile ? "Edit" : "Add buyer details")}
              </button>
            </div>
          </div>

          {profileOpen && (
            <div className="profile-form">
              <div className="form-row">
                <label htmlFor="fullName">Full name <span className="required">*</span></label>
                <input id="fullName" value={profileDraft.fullName} onChange={(e) => setProfileDraft((p) => ({ ...p, fullName: e.target.value }))} />
                {profileErrors.fullName && <div className="field-error">{profileErrors.fullName}</div>}
              </div>

              <div className="form-row">
                <label htmlFor="email">Email <span className="required">*</span></label>
                <input id="email" type="email" value={profileDraft.email} onChange={(e) => setProfileDraft((p) => ({ ...p, email: e.target.value }))} />
                {profileErrors.email && <div className="field-error">{profileErrors.email}</div>}
              </div>

              <div className="form-row">
                <label htmlFor="phone">Phone <span className="required">*</span></label>
                <input id="phone" value={profileDraft.phone} onChange={(e) => setProfileDraft((p) => ({ ...p, phone: e.target.value }))} />
                {profileErrors.phone && <div className="field-error">{profileErrors.phone}</div>}
              </div>

              <div className="form-row">
                <label htmlFor="companyName">Company / Association</label>
                <input id="companyName" value={profileDraft.companyName} onChange={(e) => setProfileDraft((p) => ({ ...p, companyName: e.target.value }))} />
              </div>

              <div className="form-row two-col">
                <div>
                  <label htmlFor="businessType">Business type</label>
                  <select id="businessType" value={profileDraft.businessType} onChange={(e) => setProfileDraft((p) => ({ ...p, businessType: e.target.value }))}>
                    <option>Importer</option>
                    <option>Exporter</option>
                    <option>Distributor</option>
                    <option>Agent</option>
                    <option>Manufacturer</option>
                    <option>Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="countries">Primary sourcing countries</label>
                  <select id="countries" value={profileDraft.countries?.join(",") || ""} onChange={(e) => {
                    const val = e.target.value;
                    const arr = val ? val.split(",").map(s => s.trim()) : [];
                    setProfileDraft((p) => ({ ...p, countries: arr }));
                  }}>
                    <option value="China">China</option>
                    <option value="Thailand">Thailand</option>
                    <option value="China,Thailand">China, Thailand</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <label htmlFor="remarks">Additional remarks</label>
                <textarea id="remarks" rows={3} value={profileDraft.remarks} onChange={(e) => setProfileDraft((p) => ({ ...p, remarks: e.target.value }))} />
              </div>

              <div className="profile-form-actions">
                <button className="btn-primary" onClick={saveProfile}>Save details</button>
                <button className="btn-outline" onClick={() => { setProfileOpen(false); setProfileDraft(userProfile || profileDraft); setProfileErrors({}); }}>Cancel</button>
              </div>

              <div className="privacy-note">
                We store this information locally to generate your inquiry PDF. It will not be sent to any server unless you choose to submit.
              </div>
            </div>
          )}
        </section>

        {/* Top actions row */}
        <div className="inquiry-actions-row">
          <div className="inquiry-actions-left">
            <button
              className="btn-outline"
              onClick={() => {
                if (window.confirm("Clear all items?")) clearInquiries();
              }}
              aria-label="Clear all inquiry items"
            >
              Clear All
            </button>
          </div>

          <div className="inquiry-actions-right">
            <button className="btn-primary" onClick={handleGeneratePDF} disabled={!isProfileValid || items.length === 0} title={!isProfileValid ? "Complete required buyer details before generating PDF" : ""}>
              Generate PDF
            </button>
          </div>
        </div>

        {(!items || items.length === 0) ? (
          <div className="inquiry-empty">
            <p>No items yet — open a product, select tags or add requirements, then click “Add to Inquiry list”.</p>
          </div>
        ) : (
          <section className="inquiry-list">
            {items.map((it) => {
              const draft = drafts[it.id] || {};
              const isEditing = editingId === it.id;
              const fullTags = Array.isArray(it.fullTags) ? it.fullTags : [];
              const selectedTags = Array.isArray(it.selectedTags) ? it.selectedTags : [];

              return (
                <article className="inquiry-card" key={it.id}>
                  <div className="inquiry-card-left">
                    {it.image ? (
                      <img src={it.image} alt={it.productName} className="inquiry-thumb" />
                    ) : (
                      <div className="inquiry-thumb placeholder" aria-hidden="true">No image</div>
                    )}
                  </div>

                  <div className="inquiry-card-body">
                    <div className="inquiry-row">
                      <h3 className="inquiry-product">{it.productName}</h3>
                      <div className="inquiry-meta">{it.country ?? "—"} • {it.category ?? "—"}</div>
                    </div>

                    <div className="inquiry-desc">{it.description}</div>

                    {/* DEFAULT READ-ONLY: only show selected tags as chips */}
                    {!isEditing && Array.isArray(selectedTags) && selectedTags.length > 0 && (
                      <div className="inquiry-tags" aria-hidden="true">
                        {selectedTags.map((tag, idx) => (
                          <span className="tag-chip" key={`${it.id}-tag-${idx}`}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* EDIT MODE: show fullTags as selectable checkboxes */}
                    {isEditing && (
                      <>
                        {fullTags && fullTags.length > 0 ? (
                          <div className="inquiry-tags-edit">
                            {fullTags.map((tag, idx) => {
                              const checked = Array.isArray(draft.selectedTags)
                                ? draft.selectedTags.includes(tag)
                                : selectedTags.includes(tag);
                              const id = `edit-${it.id}-tag-${idx}`;
                              return (
                                <label key={id} className={`tag-chip ${checked ? "checked" : ""}`} htmlFor={id}>
                                  <input
                                    id={id}
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleDraftTag(it.id, tag)}
                                  />
                                  <span className="tag-chip-label">{tag}</span>
                                </label>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="no-tags-info">No tags available to edit.</div>
                        )}
                      </>
                    )}

                    {isEditing ? (
                      <textarea
                        className="inquiry-input"
                        value={draft.additionalText ?? ""}
                        placeholder="Edit additional text"
                        onChange={(e) =>
                          setDrafts((d) => ({
                            ...d,
                            [it.id]: { ...(d[it.id] || {}), additionalText: e.target.value },
                          }))
                        }
                        rows={3}
                      />
                    ) : (
                      <div className="inquiry-additional">{it.additionalText ?? it.requirements ?? ""}</div>
                    )}

                    <div className="inquiry-card-actions">
                      {isEditing ? (
                        <>
                          <button className="btn-primary btn-small" onClick={() => saveEdit(it.id)}>Save</button>
                          <button className="btn-outline btn-small" onClick={() => setEditingId(null)}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <button className="btn-outline btn-small" onClick={() => startEdit(it)}>Edit</button>
                          <button
                            className="btn-danger btn-small"
                            onClick={() => {
                              if (window.confirm("Remove this item?")) removeInquiry(it.id);
                            }}
                          >
                            Remove
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </section>
        )}
      </main>

      <Footer />
      <ChatWithUs />

      <ProgressModal open={pdfOpen} percent={pdfPercent} pageIndex={pdfPageIndex} total={pdfTotalPages} />
    </>
  );
}